'use client';

import React, { useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Truck, ShoppingBag, DollarSign, Package, Clock, AlertCircle, Circle, CheckCircle2, Phone, MoreHorizontal, MapPin, AlertTriangle, MessageSquare } from 'lucide-react';


// ─── Types ────────────────────────────────────────────────────────────────────

type DriverStatus = 'on-route' | 'available' | 'break' | 'offline';
type OrderStatus = 'new' | 'confirmed' | 'preparing' | 'ready' | 'out-for-delivery' | 'delivered' | 'cancelled';
type IssueStatus = 'open' | 'in-progress' | 'resolved';
type IssuePriority = 'high' | 'medium' | 'low';

interface Driver {
  id: string;
  name: string;
  status: DriverStatus;
  deliveries: { done: number; total: number };
  cashCollected: string;
  cashExpected: string;
  area: string;
  lastUpdate: string;
}

interface OrderSummary {
  status: OrderStatus;
  count: number;
  label: string;
}

interface CashDriver {
  id: string;
  name: string;
  expected: number;
  collected: number;
  reconciled: boolean;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  current: number;
  minimum: number;
  unit: string;
  level: 'critical' | 'low' | 'ok';
}

interface LateDelivery {
  id: string;
  orderId: string;
  customer: string;
  area: string;
  scheduledWindow: string;
  minutesLate: number;
  driver: string;
  phone: string;
}

interface CustomerIssue {
  id: string;
  customer: string;
  phone: string;
  issue: string;
  type: string;
  priority: IssuePriority;
  status: IssueStatus;
  reportedAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const drivers: Driver[] = [
  { id: 'drv-001', name: 'Derrick Manley', status: 'on-route', deliveries: { done: 6, total: 9 }, cashCollected: '$312', cashExpected: '$342', area: "St. John's North", lastUpdate: '2 min ago' },
  { id: 'drv-002', name: 'Keisha Thomas', status: 'on-route', deliveries: { done: 4, total: 8 }, cashCollected: '$218', cashExpected: '$360', area: 'Bolans / Jennings', lastUpdate: '5 min ago' },
  { id: 'drv-003', name: 'Marcus Roberts', status: 'available', deliveries: { done: 5, total: 5 }, cashCollected: '$425', cashExpected: '$425', area: 'Parham / All Saints', lastUpdate: '12 min ago' },
  { id: 'drv-004', name: 'Yolanda Simon', status: 'break', deliveries: { done: 3, total: 6 }, cashCollected: '$165', cashExpected: '$270', area: 'English Harbour', lastUpdate: '18 min ago' },
];

const orderSummary: OrderSummary[] = [
  { status: 'new', count: 3, label: 'New' },
  { status: 'confirmed', count: 5, label: 'Confirmed' },
  { status: 'preparing', count: 8, label: 'Preparing' },
  { status: 'ready', count: 4, label: 'Ready' },
  { status: 'out-for-delivery', count: 9, label: 'On Route' },
  { status: 'delivered', count: 21, label: 'Delivered' },
  { status: 'cancelled', count: 2, label: 'Cancelled' },
];

const cashDrivers: CashDriver[] = [
  { id: 'cd-001', name: 'Derrick Manley', expected: 342, collected: 312, reconciled: false },
  { id: 'cd-002', name: 'Keisha Thomas', expected: 360, collected: 218, reconciled: false },
  { id: 'cd-003', name: 'Marcus Roberts', expected: 425, collected: 425, reconciled: true },
  { id: 'cd-004', name: 'Yolanda Simon', expected: 270, collected: 165, reconciled: false },
];

const inventoryItems: InventoryItem[] = [
  { id: 'inv-001', name: 'Chickpeas', category: 'Ingredients', current: 4, minimum: 10, unit: 'kg', level: 'critical' },
  { id: 'inv-002', name: 'Tahini', category: 'Ingredients', current: 2, minimum: 5, unit: 'kg', level: 'critical' },
  { id: 'inv-003', name: 'Small Containers', category: 'Packaging', current: 18, minimum: 30, unit: 'units', level: 'low' },
  { id: 'inv-004', name: 'Labels', category: 'Packaging', current: 0, minimum: 50, unit: 'units', level: 'critical' },
  { id: 'inv-005', name: 'Olive Oil', category: 'Ingredients', current: 3, minimum: 4, unit: 'L', level: 'low' },
  { id: 'inv-006', name: 'Medium Containers', category: 'Packaging', current: 45, minimum: 30, unit: 'units', level: 'ok' },
];

const lateDeliveries: LateDelivery[] = [
  { id: 'ld-001', orderId: 'ord-012', customer: 'Sandra Charles', area: 'English Harbour', scheduledWindow: '10:00–12:00', minutesLate: 47, driver: 'Unassigned', phone: '+1 268 770 5590' },
  { id: 'ld-002', orderId: 'ord-019', customer: 'Yvette Thomas', area: 'Friars Hill', scheduledWindow: '11:00–13:00', minutesLate: 28, driver: 'Keisha T.', phone: '+1 268 765 0934' },
  { id: 'ld-003', orderId: 'ord-023', customer: 'Calvin Peters', area: "St. John's South", scheduledWindow: '09:00–11:00', minutesLate: 15, driver: 'Derrick M.', phone: '+1 268 723 4412' },
];

const customerIssues: CustomerIssue[] = [
  { id: 'ci-001', customer: 'Natalie Joseph', phone: '+1 268 764 3201', issue: 'Wrong order delivered — received Spicy instead of Original', type: 'Wrong Item', priority: 'high', status: 'open', reportedAt: '14:22' },
  { id: 'ci-002', customer: 'Ricardo Browne', phone: '+1 268 720 8845', issue: 'Container arrived damaged and leaking', type: 'Damaged Product', priority: 'high', status: 'in-progress', reportedAt: '13:05' },
  { id: 'ci-003', customer: 'Leroy Francis', phone: '+1 268 788 2211', issue: 'Delivery 2 hours late, no notification received', type: 'Late Delivery', priority: 'medium', status: 'open', reportedAt: '12:48' },
  { id: 'ci-004', customer: 'Monique Henry', phone: '+1 268 799 1108', issue: 'Driver requested extra cash, amount unclear', type: 'Payment Issue', priority: 'medium', status: 'in-progress', reportedAt: '11:30' },
  { id: 'ci-005', customer: 'Trevor Simon', phone: '+1 268 712 6630', issue: 'Order cancelled but no refund confirmation', type: 'Cancellation', priority: 'low', status: 'resolved', reportedAt: '10:15' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const driverStatusDot: Record<DriverStatus, string> = {
  'on-route': 'text-positive',
  'available': 'text-info',
  'break': 'text-warning',
  'offline': 'text-muted-foreground',
};

const driverStatusLabel: Record<DriverStatus, string> = {
  'on-route': 'On Route',
  'available': 'Available',
  'break': 'On Break',
  'offline': 'Offline',
};

const driverStatusBg: Record<DriverStatus, string> = {
  'on-route': 'bg-positive/10 text-positive',
  'available': 'bg-info/10 text-info',
  'break': 'bg-warning/10 text-warning',
  'offline': 'bg-muted text-muted-foreground',
};

const orderStatusColor: Record<OrderStatus, string> = {
  new: 'bg-info/10 text-info border-info/20',
  confirmed: 'bg-secondary text-foreground border-border',
  preparing: 'bg-warning/10 text-warning border-warning/20',
  ready: 'bg-primary/10 text-primary border-primary/20',
  'out-for-delivery': 'bg-info/15 text-info border-info/25',
  delivered: 'bg-positive/10 text-positive border-positive/20',
  cancelled: 'bg-negative/10 text-negative border-negative/20',
};

const priorityStyles: Record<IssuePriority, string> = {
  high: 'bg-negative/10 text-negative',
  medium: 'bg-warning/10 text-warning',
  low: 'bg-muted text-muted-foreground',
};

const issueStatusStyles: Record<IssueStatus, string> = {
  open: 'bg-negative/10 text-negative',
  'in-progress': 'bg-warning/10 text-warning',
  resolved: 'bg-positive/10 text-positive',
};

const inventoryLevelBar: Record<InventoryItem['level'], string> = {
  critical: 'bg-negative',
  low: 'bg-warning',
  ok: 'bg-positive',
};

function SectionHeader({ icon, title, subtitle, action }: { icon: React.ReactNode; title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-lg bg-muted text-muted-foreground">{icon}</div>
        <div>
          <h2 className="text-base font-700 text-foreground leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function DriverStatusSection() {
  return (
    <div className="card-elevated p-5">
      <SectionHeader
        icon={<Truck size={16} />}
        title="Driver Status"
        subtitle={`${drivers.filter(d => d.status === 'on-route').length} on route · ${drivers.filter(d => d.status === 'available').length} available`}
        action={
          <button className="text-xs font-600 text-primary hover:underline">Manage drivers →</button>
        }
      />
      <div className="space-y-2.5">
        {drivers.map(driver => {
          const progress = Math.round((driver.deliveries.done / driver.deliveries.total) * 100);
          return (
            <div key={driver.id} className="p-3 rounded-xl bg-muted/40 hover:bg-muted/70 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-xs font-700 text-white flex-shrink-0">
                  {driver.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5">
                      <Circle size={7} className={`fill-current ${driverStatusDot[driver.status]} flex-shrink-0`} />
                      <p className="text-sm font-600 text-foreground">{driver.name}</p>
                    </div>
                    <span className={`text-[10px] font-700 px-2 py-0.5 rounded-full ${driverStatusBg[driver.status]}`}>
                      {driverStatusLabel[driver.status]}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <span className="flex items-center gap-1"><MapPin size={10} />{driver.area}</span>
                    <span className="font-tabular">{driver.deliveries.done}/{driver.deliveries.total} deliveries</span>
                  </div>
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-xs">
                    <span className="text-muted-foreground">Cash: <span className="font-tabular font-600 text-foreground">{driver.cashCollected}</span> / {driver.cashExpected}</span>
                    <span className="text-muted-foreground">{driver.lastUpdate}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderStatusSection() {
  const total = orderSummary.reduce((s, o) => s + o.count, 0);
  const delivered = orderSummary.find(o => o.status === 'delivered')?.count ?? 0;
  const deliveryRate = Math.round((delivered / total) * 100);

  return (
    <div className="card-elevated p-5">
      <SectionHeader
        icon={<ShoppingBag size={16} />}
        title="Order Status"
        subtitle={`${total} orders today · ${deliveryRate}% delivery rate`}
        action={
          <button className="text-xs font-600 text-primary hover:underline">View pipeline →</button>
        }
      />
      <div className="grid grid-cols-2 gap-2 mb-4">
        {orderSummary.map(item => (
          <div
            key={item.status}
            className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${orderStatusColor[item.status]}`}
          >
            <span className="text-xs font-600">{item.label}</span>
            <span className="text-lg font-700 font-tabular leading-none">{item.count}</span>
          </div>
        ))}
      </div>
      {/* Progress bar breakdown */}
      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>Fulfilment progress</span>
          <span className="font-tabular font-600 text-foreground">{deliveryRate}%</span>
        </div>
        <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden flex gap-0.5">
          {orderSummary.map(item => {
            const pct = (item.count / total) * 100;
            const colorMap: Record<OrderStatus, string> = {
              new: 'bg-info/60',
              confirmed: 'bg-secondary-foreground/20',
              preparing: 'bg-warning/70',
              ready: 'bg-primary/70',
              'out-for-delivery': 'bg-info',
              delivered: 'bg-positive',
              cancelled: 'bg-negative/60',
            };
            return (
              <div
                key={item.status}
                className={`h-full ${colorMap[item.status]} transition-all duration-500`}
                style={{ width: `${pct}%` }}
                title={`${item.label}: ${item.count}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CashReconciliationSection() {
  const totalExpected = cashDrivers.reduce((s, d) => s + d.expected, 0);
  const totalCollected = cashDrivers.reduce((s, d) => s + d.collected, 0);
  const difference = totalCollected - totalExpected;
  const pendingCount = cashDrivers.filter(d => !d.reconciled).length;

  return (
    <div className="card-elevated p-5">
      <SectionHeader
        icon={<DollarSign size={16} />}
        title="Cash Reconciliation"
        subtitle={`${pendingCount} drivers pending approval`}
        action={
          <span className={`text-xs font-700 px-2.5 py-1 rounded-full ${difference === 0 ? 'bg-positive/10 text-positive' : 'bg-warning/10 text-warning'}`}>
            {difference === 0 ? 'Balanced' : `${difference > 0 ? '+' : ''}$${Math.abs(difference)}`}
          </span>
        }
      />
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Expected', value: `$${totalExpected.toLocaleString()}`, color: 'text-foreground' },
          { label: 'Collected', value: `$${totalCollected.toLocaleString()}`, color: 'text-foreground' },
          { label: 'Variance', value: `${difference >= 0 ? '+' : ''}$${difference}`, color: difference === 0 ? 'text-positive' : 'text-warning' },
        ].map(item => (
          <div key={item.label} className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground mb-1">{item.label}</p>
            <p className={`text-lg font-700 font-tabular leading-none ${item.color}`}>{item.value}</p>
          </div>
        ))}
      </div>
      {/* Per-driver rows */}
      <div className="space-y-2">
        {cashDrivers.map(driver => {
          const diff = driver.collected - driver.expected;
          return (
            <div key={driver.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
              <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-[10px] font-700 text-white flex-shrink-0">
                {driver.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-600 text-foreground truncate">{driver.name}</p>
                <p className="text-xs text-muted-foreground font-tabular">
                  ${driver.collected} / ${driver.expected}
                  {diff !== 0 && (
                    <span className={`ml-1.5 font-700 ${diff > 0 ? 'text-positive' : 'text-warning'}`}>
                      ({diff > 0 ? '+' : ''}${diff})
                    </span>
                  )}
                </p>
              </div>
              {driver.reconciled ? (
                <span className="flex items-center gap-1 text-[10px] font-700 text-positive bg-positive/10 px-2 py-0.5 rounded-full">
                  <CheckCircle2 size={10} /> Approved
                </span>
              ) : (
                <button className="text-[10px] font-700 text-primary bg-primary/10 hover:bg-primary/20 px-2.5 py-1 rounded-full transition-colors">
                  Approve
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InventoryLevelsSection() {
  const criticalCount = inventoryItems.filter(i => i.level === 'critical').length;
  const lowCount = inventoryItems.filter(i => i.level === 'low').length;

  return (
    <div className="card-elevated p-5">
      <SectionHeader
        icon={<Package size={16} />}
        title="Inventory Levels"
        subtitle={`${criticalCount} critical · ${lowCount} low`}
        action={
          <button className="text-xs font-600 text-primary hover:underline">Full inventory →</button>
        }
      />
      <div className="space-y-2.5">
        {inventoryItems.map(item => {
          const pct = item.current === 0 ? 0 : Math.min(100, Math.round((item.current / (item.minimum * 2)) * 100));
          return (
            <div key={item.id} className="group">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  {item.level === 'critical' && <AlertTriangle size={12} className="text-negative flex-shrink-0" />}
                  {item.level === 'low' && <AlertTriangle size={12} className="text-warning flex-shrink-0" />}
                  {item.level === 'ok' && <CheckCircle2 size={12} className="text-positive flex-shrink-0" />}
                  <span className="text-sm font-600 text-foreground">{item.name}</span>
                  <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{item.category}</span>
                </div>
                <span className={`text-xs font-700 font-tabular ${item.level === 'critical' ? 'text-negative' : item.level === 'low' ? 'text-warning' : 'text-positive'}`}>
                  {item.current} {item.unit}
                </span>
              </div>
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${inventoryLevelBar[item.level]}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">Min: {item.minimum} {item.unit}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LateDeliveriesSection() {
  return (
    <div className="card-elevated p-5">
      <SectionHeader
        icon={<Clock size={16} />}
        title="Late Deliveries"
        subtitle={`${lateDeliveries.length} deliveries past window`}
        action={
          <span className="text-xs font-700 px-2.5 py-1 rounded-full bg-negative/10 text-negative">
            {lateDeliveries.length} Late
          </span>
        }
      />
      {lateDeliveries.length === 0 ? (
        <div className="py-8 text-center">
          <CheckCircle2 size={28} className="text-positive mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">All deliveries on time</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lateDeliveries.map(delivery => (
            <div key={delivery.id} className="p-3 rounded-xl border border-negative/20 bg-negative/5 hover:bg-negative/8 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div>
                  <p className="text-sm font-700 text-foreground">{delivery.customer}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin size={10} />{delivery.area} · Window: {delivery.scheduledWindow}
                  </p>
                </div>
                <span className="text-xs font-700 text-negative bg-negative/10 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                  +{delivery.minutesLate} min
                </span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Driver: <span className={`font-600 ${delivery.driver === 'Unassigned' ? 'text-warning' : 'text-foreground'}`}>
                    {delivery.driver}
                  </span>
                </p>
                <div className="flex items-center gap-1.5">
                  <button className="flex items-center gap-1 text-[10px] font-600 text-primary bg-primary/10 hover:bg-primary/20 px-2 py-1 rounded-full transition-colors">
                    <Phone size={10} /> Call
                  </button>
                  <button className="flex items-center gap-1 text-[10px] font-600 text-muted-foreground bg-muted hover:bg-secondary px-2 py-1 rounded-full transition-colors">
                    <MoreHorizontal size={10} /> Actions
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerIssuesSection() {
  const [filter, setFilter] = useState<'all' | IssueStatus>('all');
  const openCount = customerIssues.filter(i => i.status === 'open').length;
  const filtered = filter === 'all' ? customerIssues : customerIssues.filter(i => i.status === filter);

  return (
    <div className="card-elevated p-5">
      <SectionHeader
        icon={<MessageSquare size={16} />}
        title="Customer Issues"
        subtitle={`${openCount} open · ${customerIssues.length} total today`}
        action={
          <button className="text-xs font-600 text-primary hover:underline">View all →</button>
        }
      />
      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto scrollbar-thin pb-1">
        {(['all', 'open', 'in-progress', 'resolved'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-xs font-600 whitespace-nowrap transition-all duration-150 ${
              filter === f
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            {f === 'all' ? 'All' : f === 'in-progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
            <span className="ml-1.5 font-tabular">
              {f === 'all' ? customerIssues.length : customerIssues.filter(i => i.status === f).length}
            </span>
          </button>
        ))}
      </div>
      <div className="space-y-2.5">
        {filtered.map(issue => (
          <div key={issue.id} className="p-3 rounded-xl bg-muted/40 hover:bg-muted/70 transition-colors">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <p className="text-sm font-700 text-foreground">{issue.customer}</p>
                  <span className={`text-[10px] font-700 px-1.5 py-0.5 rounded-full ${priorityStyles[issue.priority]}`}>
                    {issue.priority.toUpperCase()}
                  </span>
                  <span className={`text-[10px] font-700 px-1.5 py-0.5 rounded-full ${issueStatusStyles[issue.status]}`}>
                    {issue.status === 'in-progress' ? 'In Progress' : issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5 inline-block mb-1">{issue.type}</p>
                <p className="text-xs text-foreground/80 leading-relaxed">{issue.issue}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-[10px] text-muted-foreground">Reported at {issue.reportedAt}</p>
              <div className="flex items-center gap-1.5">
                <button className="flex items-center gap-1 text-[10px] font-600 text-primary bg-primary/10 hover:bg-primary/20 px-2 py-1 rounded-full transition-colors">
                  <Phone size={10} /> Call
                </button>
                {issue.status !== 'resolved' && (
                  <button className="flex items-center gap-1 text-[10px] font-600 text-positive bg-positive/10 hover:bg-positive/20 px-2 py-1 rounded-full transition-colors">
                    <CheckCircle2 size={10} /> Resolve
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="py-8 text-center">
            <CheckCircle2 size={28} className="text-positive mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No issues in this category</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Summary KPI Strip ────────────────────────────────────────────────────────

function ManagerKPIStrip() {
  const kpis = [
    { id: 'mkpi-1', label: 'Total Orders', value: '52', sub: '21 delivered', icon: <ShoppingBag size={16} />, color: 'text-info' },
    { id: 'mkpi-2', label: 'Active Drivers', value: '4', sub: '2 on route', icon: <Truck size={16} />, color: 'text-positive' },
    { id: 'mkpi-3', label: 'Cash Collected', value: '$1,120', sub: '$340 pending', icon: <DollarSign size={16} />, color: 'text-primary' },
    { id: 'mkpi-4', label: 'Inventory Alerts', value: '4', sub: '2 critical', icon: <Package size={16} />, color: 'text-negative' },
    { id: 'mkpi-5', label: 'Late Deliveries', value: '3', sub: 'Needs action', icon: <Clock size={16} />, color: 'text-warning' },
    { id: 'mkpi-6', label: 'Open Issues', value: '2', sub: '1 high priority', icon: <AlertCircle size={16} />, color: 'text-negative' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      {kpis.map(kpi => (
        <div key={kpi.id} className="card-elevated p-4 flex flex-col gap-2">
          <div className={`${kpi.color}`}>{kpi.icon}</div>
          <div>
            <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground">{kpi.label}</p>
            <p className="text-2xl font-700 font-tabular text-foreground leading-none mt-0.5">{kpi.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{kpi.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ManagerDashboardPage() {
  const [lastRefresh] = useState('16:58');

  return (
    <AdminLayout
      title="Manager Dashboard"
      subtitle={`Wednesday, 30 July 2026 · Last updated ${lastRefresh}`}
    >
      {/* KPI Strip */}
      <ManagerKPIStrip />

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Driver Status */}
        <div className="xl:col-span-1">
          <DriverStatusSection />
        </div>

        {/* Order Status */}
        <div className="xl:col-span-1">
          <OrderStatusSection />
        </div>

        {/* Cash Reconciliation */}
        <div className="xl:col-span-1">
          <CashReconciliationSection />
        </div>

        {/* Inventory Levels */}
        <div className="xl:col-span-1">
          <InventoryLevelsSection />
        </div>

        {/* Late Deliveries */}
        <div className="xl:col-span-1">
          <LateDeliveriesSection />
        </div>

        {/* Customer Issues */}
        <div className="xl:col-span-1">
          <CustomerIssuesSection />
        </div>
      </div>
    </AdminLayout>
  );
}
